import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { DataService } from '../../data.service';

/**
 *  ÉRTÉKELÉS-TÍPUSOK (röpdolgozat, témazáró, felelet…)
 *
 *  A típus nem csak címke: a SÚLYA dönti el, mennyit nyom az átlagban, a SZÍNE pedig
 *  azt, mennyire ugrik ki a jegy a naplóban. Ez iskolánként eltérő gyakorlat – ahol a
 *  témazáró háromszoros, ott az nem lehet kódmódosítás kérdése.
 *
 *  A közös (MAIN) típusok is közvetlenül szerkeszthetők – a beírt jegyek ezekre hivatkoznak,
 *  egy átvett másolat súlya egyikre sem hatna. Csak a két „-” nevű TECHNIKAI típus zárolt
 *  (a szöveges félévi/év végi értékelésé): a felület a nevük alapján ismeri fel őket.
 *
 *  Törölni csak a sehol nem hivatkozott típust lehet; a használatban lévőt ELREJTENI: a tanárok
 *  választóiban nem jelenik meg, a már beírt jegyeknél viszont látszik és számít.
 */
@Component({
  selector: 'app-admin-ertekeles-tipus',
  templateUrl: './ertekeles-tipus.component.html',
  styleUrls: ['./ertekeles-tipus.component.scss'],
})
export class AdminErtekelesTipusComponent implements OnInit {
  sajatIntezmeny: any;
  tipusok: any[] = [];
  toltodik = false;
  hiba = '';
  uj: any = { nev: '', szorzo: 100, szin: '#000000', kreta: '' };
  ujNyitva = false;

  /*  JEGYBEÍRÁS MÓDJA. Az alapérték a gombos – az iskola ezt kérte –, de kapcsolható:
   *  ahol százalékkal vagy szöveggel értékelnek, ott a gombsor nem segítene. */
  jegyGombok = true;
  beallitasMentes = false;

  /*  JEGYBEÍRÁSI (ÉRTESÍTÉSI) HATÁRIDŐ – 20/2012. EMMI r. 64. § (1a): a pedagógiai program
   *  rögzíti, a jegy napjától hány napon belül kell a jegyet a naplóba beírni. Üres = nincs
   *  figyelés. Csak jelez: a tanár a mentéskor figyelmeztetést kap, itt pedig tanáronként
   *  látszik, kinél és mennyit késett a beírás. */
  hataridoNap: number | null = null;
  hataridoBemenet = '';
  hataridoMentes = false;

  kesveTol = '';
  kesveIg = '';
  kesve: any = null;
  kesveToltodik = false;
  kesveHiba = '';
  /** A kinyitott tanár tételes listája (adid → jegyek). */
  kesveNyitott: string | null = null;
  kesveTetelek: any[] = [];
  kesveTetelToltodik = false;

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    this.sajatIntezmeny = await this.data.getFromStorage('sajatIntezmeny');
    await this.betolt();
    await this.beallitasBetolt();
  }

  private async beallitasBetolt() {
    if (!this.intezmeny) return;
    try {
      const v: any = await firstValueFrom(this.http.get(`/api/ertekeles/beallitas/${this.intezmeny}`));
      this.jegyGombok = v?.jegy_gombok !== false;
      const h = v?.jegybeirasi_hatarido_nap;
      this.hataridoNap = h === null || h === undefined || h === '' ? null : Number(h);
      this.hataridoBemenet = this.hataridoNap === null ? '' : String(this.hataridoNap);
    } catch (e) { /* marad az alapértelmezés (gombos, határidő nélkül) */ }
  }

  get hataridoModosult(): boolean {
    const most = String(this.hataridoBemenet ?? '').trim();
    return most !== (this.hataridoNap === null ? '' : String(this.hataridoNap));
  }

  async hataridoMent() {
    if (!this.intezmeny) return;
    const nyers = String(this.hataridoBemenet ?? '').trim();
    const ertek = nyers === '' ? null : Number(nyers);
    if (ertek !== null && (!Number.isInteger(ertek) || ertek < 0 || ertek > 365)) {
      this.data.sendToast('A határidő 0 és 365 közötti egész nap lehet (üresen: nincs figyelés).', 'warning');
      return;
    }
    this.hataridoMentes = true;
    try {
      const v: any = await firstValueFrom(this.http.post(`/api/ertekeles/beallitas/${this.intezmeny}`,
        { jegybeirasi_hatarido_nap: ertek }));
      this.hataridoNap = v?.jegybeirasi_hatarido_nap ?? null;
      this.hataridoBemenet = this.hataridoNap === null ? '' : String(this.hataridoNap);
      this.data.sendToast(this.hataridoNap === null
        ? 'A jegybeírási határidő figyelése kikapcsolva.'
        : `Jegybeírási határidő: a jegy napjától ${this.hataridoNap} nap.`, 'success');
      if (this.kesve) await this.kesveBetolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A határidő mentése nem sikerült.', 'error');
    } finally {
      this.hataridoMentes = false;
    }
  }

  private kesveParameterek(tanar?: string): string {
    const p = new URLSearchParams();
    if (this.kesveTol) p.set('tol', this.kesveTol);
    if (this.kesveIg) p.set('ig', this.kesveIg);
    if (tanar) p.set('tanar', tanar);
    const s = p.toString();
    return s ? `?${s}` : '';
  }

  /** Késve beírt jegyek tanáronként (a jegy napjára szűrve; alap: a tanév eleje – ma). */
  async kesveBetolt() {
    if (!this.intezmeny) return;
    this.kesveToltodik = true;
    this.kesveHiba = '';
    this.kesveNyitott = null;
    this.kesveTetelek = [];
    try {
      this.kesve = await firstValueFrom(
        this.http.get(`/api/ertekeles/kesve-beirt/${this.intezmeny}${this.kesveParameterek()}`));
      if (!this.kesveTol && this.kesve?.idoszak?.tol) this.kesveTol = this.kesve.idoszak.tol;
      if (!this.kesveIg && this.kesve?.idoszak?.ig) this.kesveIg = this.kesve.idoszak.ig;
    } catch (e: any) {
      this.kesve = null;
      this.kesveHiba = e?.error?.error || 'A lista nem tölthető be.';
    } finally {
      this.kesveToltodik = false;
    }
  }

  async kesveTanar(t: any) {
    if (this.kesveNyitott === t.adid) { this.kesveNyitott = null; this.kesveTetelek = []; return; }
    this.kesveNyitott = t.adid;
    this.kesveTetelek = [];
    this.kesveTetelToltodik = true;
    try {
      const v: any = await firstValueFrom(
        this.http.get(`/api/ertekeles/kesve-beirt/${this.intezmeny}${this.kesveParameterek(t.adid)}`));
      this.kesveTetelek = Array.isArray(v?.jegyek) ? v.jegyek : [];
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A tételes lista nem tölthető be.', 'error');
      this.kesveNyitott = null;
    } finally {
      this.kesveTetelToltodik = false;
    }
  }

  get kesveOsszes(): number {
    return (this.kesve?.tanarok || []).reduce((s: number, t: any) => s + (Number(t.db) || 0), 0);
  }

  async jegyGombokMent() {
    if (!this.intezmeny) return;
    this.beallitasMentes = true;
    try {
      await firstValueFrom(this.http.post(`/api/ertekeles/beallitas/${this.intezmeny}`,
        { jegy_gombok: this.jegyGombok }));
      this.data.sendToast(this.jegyGombok
        ? 'A tanárok mostantól gombokkal írják be a jegyet.'
        : 'A tanárok mostantól szövegmezőbe írják a jegyet.', 'success');
    } catch (e: any) {
      //  Vissza a korábbi állásba: a kapcsoló ne mutasson olyasmit, ami nem mentődött el.
      this.jegyGombok = !this.jegyGombok;
      this.data.sendToast(e?.error?.error || 'A beállítás mentése nem sikerült.', 'error');
    } finally {
      this.beallitasMentes = false;
    }
  }

  get intezmeny(): string {
    return String(this.sajatIntezmeny?.intezmenyId || '').replace(/^\/+|\/+$/g, '');
  }

  async betolt() {
    if (!this.intezmeny) return;
    this.toltodik = true;
    this.hiba = '';
    try {
      const r: any = await firstValueFrom(
        this.http.get(`/api/ertekeles/tipus-kezeles/${this.intezmeny}`));
      this.tipusok = (Array.isArray(r) ? r : []).map((t: any) => ({ ...t, eredeti: { ...t } }));
    } catch (e: any) {
      this.hiba = e?.error?.error || 'A típusok nem tölthetők be.';
    } finally {
      this.toltodik = false;
    }
  }

  /** Változott-e a sor a betöltött állapothoz képest. */
  modosult(t: any): boolean {
    return t.nev !== t.eredeti.nev || Number(t.szorzo) !== Number(t.eredeti.szorzo)
      || (t.szin || '') !== (t.eredeti.szin || '') || (t.kreta || '') !== (t.eredeti.kreta || '');
  }

  /** A súly emberi alakja: a tárolt 100 = 1×, 200 = 2×. */
  szorzoSzoveg(t: any): string {
    const n = Number(t?.szorzo);
    if (!Number.isFinite(n)) return '';
    return `${(n / 100).toLocaleString('hu-HU', { maximumFractionDigits: 2 })}×`;
  }

  async ment(t: any) {
    if (t.technikai) {
      this.data.sendToast('Technikai típus – nem módosítható.', 'warning');
      return;
    }
    const nev = String(t.nev || '').trim();
    if (!nev) { this.data.sendToast('Adj nevet a típusnak.', 'warning'); return; }
    if (nev === '-') { this.data.sendToast('A „-” név a technikai típusoké.', 'warning'); return; }
    try {
      await firstValueFrom(this.http.put(
        `/api/ertekeles/tipus-kezeles/${t.id}/${this.intezmeny}`,
        { nev, szorzo: t.szorzo, szin: t.szin, kreta: t.kreta }));
      this.data.sendToast('Mentve.', 'success');
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A mentés nem sikerült.', 'error');
    }
  }

  async ujMent() {
    const nev = String(this.uj.nev || '').trim();
    if (!nev) { this.data.sendToast('Adj nevet a típusnak.', 'warning'); return; }
    if (nev === '-') { this.data.sendToast('A „-” név a technikai típusoké.', 'warning'); return; }
    try {
      await firstValueFrom(this.http.post(
        `/api/ertekeles/tipus-kezeles/${this.intezmeny}`, { ...this.uj, nev }));
      this.data.sendToast('Típus felvéve.', 'success');
      this.uj = { nev: '', szorzo: 100, szin: '#000000', kreta: '' };
      this.ujNyitva = false;
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A felvétel nem sikerült.', 'error');
    }
  }

  /** Törölhető-e (a gomb állapotához): csak a sehol nem hivatkozott, nem technikai típus. */
  torolheto(t: any): boolean {
    return !t.technikai && !t.hasznalat && !t.szamonkeres;
  }
  torlesCim(t: any): string {
    if (t.technikai) return 'Technikai típus – nem törölhető';
    if (t.hasznalat || t.szamonkeres) {
      return 'Használatban van (' + [t.hasznalat ? `${t.hasznalat} jegy` : '', t.szamonkeres ? `${t.szamonkeres} számonkérés` : '']
        .filter(Boolean).join(', ') + ') – nem törölhető, de elrejthető';
    }
    return 'Törlés';
  }

  async rejtes(t: any) {
    const rejtett = !t.rejtett;
    try {
      await firstValueFrom(this.http.put(
        `/api/ertekeles/tipus-kezeles/${t.id}/rejtes/${this.intezmeny}`, { rejtett }));
      this.data.sendToast(rejtett
        ? `„${t.nev}" elrejtve – a tanárok nem választhatják, a meglévő jegyek maradnak.`
        : `„${t.nev}" újra választható.`, 'success');
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A módosítás nem sikerült.', 'error');
    }
  }

  async torol(t: any) {
    if (!confirm(`Törlöd a(z) „${t.nev}" típust?`
      + (t.kozos ? '\n\nEz közös típus: a rendszer minden intézményénél megszűnik.' : ''))) return;
    try {
      await firstValueFrom(this.http.delete(
        `/api/ertekeles/tipus-kezeles/${t.id}/${this.intezmeny}`));
      this.data.sendToast('Törölve.', 'success');
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A törlés nem sikerült.', 'error');
    }
  }
}
